import React, {useEffect, useMemo} from "react"
import {BackButton} from "../../components/BackButton/BackButton"
import {useHistory, useLocation} from "react-router-dom"
import {useFormik} from "formik"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import * as yup from "yup"
import {Helmet} from "react-helmet-async"
import {Button, Checkbox, Form, Tag} from "antd"
import {FormInput} from "../../components/Form/FormInput"
import {getCompanies, setCompanies} from "../../store/companyReducer"
import {FormSelect} from "../../components/Form/FormSelect"
import {changeUserGroup, createUserGroup} from "../../store/userGroupReducer"
import {ID_SYSTEM_COMPANY} from "../../constants/constants"
import {UserGroupResponse} from "../../models/response"

const schema = yup.object().shape({
    name: yup.string().required('Введите название').max(250, 'Максимальная длина 250 символов'),
    idCompany: yup.number().required('Выберите компанию').min(1, 'Выберите компанию'),
})

export const UserGroupForm = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation<{ userGroup: UserGroupResponse, idCompany?: number }>()
    const userGroup = location?.state?.userGroup
    const idCompany = location?.state?.idCompany
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const status = useSelector((state: StateType) => state.appReducer.status)
    const companies = useSelector((state: StateType) => state.companyReducer.companies)
    const controller = useMemo(() => new AbortController(), [])

    useEffect(() => {
        dispatch(getCompanies({controller}))

        return () => {
            controller.abort()
            dispatch(setCompanies(null))
        }
    }, [dispatch, controller])

    const formik = useFormik({
        initialValues: {
            name: userGroup?.name || '',
            idCompany: userGroup?.idCompany || idCompany,
            active: userGroup ? userGroup.active : true,
        },
        validationSchema: schema,
        onSubmit: async (values) => {
            let res: any
            if (userGroup) {
                res = await dispatch(changeUserGroup({
                    id: userGroup.id,
                    name: values.name,
                    idCompany: values.idCompany || 0,
                    active: values.active,
                    controller,
                }))
            } else {
                res = await dispatch(createUserGroup({
                    name: values.name,
                    idCompany: values.idCompany || 0,
                    active: values.active,
                    controller,
                }))
            }
            if (res?.payload?.status === 0) {
                if (!userGroup)
                {
                    history.replace(`/userGroup/${res.payload.data.id}`)
                }
                else
                {
                    history.goBack()
                }
            }
        },
    })
    const {values, handleChange, handleBlur, handleSubmit, errors, touched, setFieldValue} = formik

    useEffect(() => {
        if (companies?.data && !values?.idCompany) {
            setFieldValue('idCompany', companies.data[0]?.id)
        }
    }, [companies, values?.idCompany])

    const companyOptions = useMemo(() =>
        companies?.data
            ?.filter(i => userGroup ? userGroup.idCompany === ID_SYSTEM_COMPANY ? i.id === ID_SYSTEM_COMPANY : i.id !== ID_SYSTEM_COMPANY : true)
            ?.map(i => ({value: i.id, label: i.name})) || [], [companies, userGroup])

    return (
        <div className="form-page">
            <Helmet>
                <title>{userGroup ? 'Обновление группы' : 'Новая группа'}</title>
            </Helmet>
            <Form className="form-container"
                  autoComplete="off"
                  onSubmitCapture={handleSubmit}
            >
                <div className="description-header"><BackButton onClick={() => history.goBack()}/></div>

                <div className="description-title">{userGroup ? 'Обновление группы' : 'Новая группа'}</div>

                <FormInput placeholder="Название"
                           value={values.name}
                           name="name"
                           onBlur={handleBlur}
                           onChange={handleChange}
                           isInvalid={touched.name && errors.name}
                           allowClear
                />
                {
                    profile?.idCompany === ID_SYSTEM_COMPANY
                        ? <FormSelect placeholder="Компания"
                                      value={values.idCompany || ''}
                                      onChange={(value) => setFieldValue('idCompany', value)}
                                      options={companyOptions}
                                      className="form-select"
                                      isInvalid={touched.idCompany && errors.idCompany}
                        />
                        : null
                }

                <div style={{marginTop: 20}}>
                    <Checkbox checked={values.active}
                              name="active"
                              onChange={handleChange}
                    >
                        <Tag color={values.active ? 'green' : 'default'}>Активно</Tag>
                    </Checkbox>
                </div>

                <div className="form-button-block">
                    <Button type="primary"
                            htmlType="submit"
                            loading={status === 'loading'}
                    >
                        Сохранить
                    </Button>
                    <Button type="default"
                            onClick={() => history.goBack()}
                    >
                        Отмена
                    </Button>
                </div>
            </Form>
        </div>
    )
}

